import {
    useImperativeHandle,
    useLayoutEffect,
    useEffect,
    useState,
    Fragment,
    useRef,
    useId
} from "react";
import {
    Platform,
    View
} from "react-native";
import {
    type SelectSheetRowAccessibilityType,
    type SelectedItem
} from "./type";
import type ISelectSheetProps from "./type";
import stylesheet, {
    SELECT_SHEET_STANDALONE_GROUP_KEY,
    SELECT_SHEET_SELECTED_BACKGROUND,
    getSelectSheetIsDividerVisible,
    SELECT_SHEET_ROW_TITLE_VARIANT,
    getSelectSheetSelection,
    useStyles
} from "./stylesheet";
import {
    getSelectSheetSearchedData,
    subscribeSelectSheetPress,
    focusSelectSheetOpener,
    getSelectSheetItemKey
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../types";
import type {
    IBottomSheetRef
} from "../bottomSheet/type";
import type {
    LoadingSize
} from "../loading/type";
import type {
    ITextInputRef
} from "../textInput/type";
import {
    Search as SearchIcon,
    Check as CheckIcon,
    Info as InfoIcon
} from "lucide-react-native";
import {
    getSelectionGroups
} from "../../helpers/selection";
import {
    formatNumber
} from "../../helpers/locale/format";
import {
    getItemTitleVariant
} from "../item/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import PopoverSurfaceHeading from "../popoverSurface/components/heading";
import BottomSheet from "../bottomSheet";
import TextInput from "../textInput";
import CheckBox from "../checkBox";
import Loading from "../loading";
import Button from "../button";
import Empty from "../empty";
import Item from "../item";
import Text from "../text";

const SelectAllIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <CheckIcon
    color={customColor}
    size={size}
/>;

const SearchFieldIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <SearchIcon
    color={customColor}
    size={size}
/>;

function SelectSheet<T>({
    setIsMoreLoading: setIsMoreLoadingProp,
    customLocalize: customLocalizeProp,
    setSearchText: setSearchTextProp,
    keyExtractor: keyExtractorProp,
    searchedData: searchedDataProp,
    selectBoxKey: selectBoxKeyProp,
    setIsLoading: setIsLoadingProp,
    customTheme: customThemeProp,
    searchText: searchTextProp,
    isWorkWithRealtime = true,
    LoadingIconComponentProp,
    isMoreLoading = false,
    isOpen: isOpenProp,
    isShowTools = true,
    isLoading = false,
    mainSelectedItems,
    moreLoadThreshold,
    bottomSheetProps,
    isMultipleSelect,
    renderOptionIcon,
    bottomSheetRef,
    groupExtractor,
    returnFocusRef,
    isSearchable,
    onOpenChange,
    selectObject,
    description,
    setIsActive,
    onMoreLoad,
    renderItem,
    customKey,
    maxChoice,
    minChoice,
    selectAll,
    cleanAll,
    clearAll,
    isActive,
    cancel,
    title,
    data,
    ok
}: ISelectSheetProps<T>) {
    const customLocalize = customLocalizeProp ?? bottomSheetProps?.customLocalize;

    const customTheme = customThemeProp ?? bottomSheetProps?.customTheme;

    const {
        controlHeights,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isOpen = isOpenProp ?? isActive ?? false;

    const baseId = useId();

    const sheetRef = useRef<IBottomSheetRef>(null);

    const searchInputRef = useRef<ITextInputRef>(null);

    const [
        internalSearchText,
        setInternalSearchText
    ] = useState("");

    useEffect(() => {
        if(cleanAll === undefined) {
            return;
        }

        warnOnce({
            message: "SelectSheet: \"cleanAll\" is deprecated and will be removed in the next major version. Use \"clearAll\" instead."
        });
    }, [
        cleanAll
    ]);

    useEffect(() => {
        if(isActive === undefined) {
            return;
        }

        warnOnce({
            message: "SelectSheet: \"isActive\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
        });
    }, [
        isActive
    ]);

    useEffect(() => subscribeSelectSheetPress(), []);

    useLayoutEffect(() => {
        if(!isOpen) {
            return;
        }

        focusSelectSheetOpener();
    }, [
        isOpen
    ]);

    useImperativeHandle(
        bottomSheetRef,
        () => ({
            setIsContentReady: (status) => {
                sheetRef.current?.setIsContentReady(status);
            },
            close: (onClosedCallback?) => {
                sheetRef.current?.close(onClosedCallback);
            },
            open: () => {
                sheetRef.current?.open();
            }
        }),
        []
    );

    const listId = `${baseId}-list`;

    const helperId = `${baseId}-helper`;

    const searchText = searchTextProp ?? internalSearchText;

    const selectBoxKey = selectBoxKeyProp ?? baseId;

    const keyExtractor = keyExtractorProp ?? getSelectSheetItemKey;

    const setIsLoading = setIsLoadingProp ?? (() => undefined);

    const setIsMoreLoading = setIsMoreLoadingProp ?? (() => undefined);

    const clearSelection = clearAll ?? cleanAll;

    const isSearching = searchText.length > 0;

    const getCurrentData = () => {
        if(!isSearching) {
            return data;
        }

        return searchedDataProp ?? getSelectSheetSearchedData({
            locale: activeLocale,
            searchText,
            data
        });
    };

    const getItemGroup = (item: T & SelectedItem | SelectedItem) => groupExtractor ? groupExtractor(item) ?? "" : "";

    const groups = groupExtractor ? getSelectionGroups({
        getHeading: (item) => {
            const group = getItemGroup(item);

            return group === SELECT_SHEET_STANDALONE_GROUP_KEY ? undefined : group || undefined;
        },
        entries: getCurrentData(),
        getKey: getItemGroup
    }) : undefined;

    const currentData = groups ? groups.flatMap((group) => group.options) : getCurrentData();

    const currentSelection = getSelectSheetSelection({
        selectedItems: mainSelectedItems,
        maxChoice,
        minChoice
    });

    const highlightedKeys = isMultipleSelect ? new Set<string>() : currentSelection.selectedKeys;

    const isToolsVisible = isShowTools && !!isMultipleSelect;

    const isControlsVisible = !!isSearchable || isToolsVisible;

    const sheetTitle = title ?? bottomSheetProps?.title;

    const {
        footerActions: footerActionsDynamicStyle,
        hiddenDivider: hiddenDividerDynamicStyle,
        checkBoxSlot: checkBoxSlotDynamicStyle,
        footerStatus: footerStatusDynamicStyle,
        groupHeading: groupHeadingDynamicStyle,
        moreLoading: moreLoadingDynamicStyle,
        emptyState: emptyStateDynamicStyle,
        rowLeading: rowLeadingDynamicStyle,
        controls: controlsDynamicStyle,
        divider: dividerDynamicStyle,
        loading: loadingDynamicStyle,
        footer: footerDynamicStyle,
        tools: toolsDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        controlHeights,
        iconSizes,
        borders,
        colors,
        spaces
    });

    const handleSearchTextChange = (text: string) => {
        setInternalSearchText(text);

        if(setSearchTextProp) setSearchTextProp(text);
    };

    const handleClearSearch = () => {
        handleSearchTextChange("");

        searchInputRef.current?.focus();
    };

    const handleCancel = () => {
        onOpenChange?.(false);

        if(cancel) {
            cancel();

            return;
        }

        sheetRef.current?.close();
    };

    const handleOk = () => {
        if(ok) {
            ok();

            return;
        }

        onOpenChange?.(false);

        sheetRef.current?.close();
    };

    const onSheetScrollEnd = () => {
        if(bottomSheetProps?.onScrollEnd) {
            bottomSheetProps.onScrollEnd();
        }

        if(!onMoreLoad) {
            return;
        }

        onMoreLoad({
            selectedItems: mainSelectedItems,
            setIsMoreLoading,
            setIsLoading,
            searchText,
            data
        });
    };

    const handleSheetOpenChange = (isNextOpen: boolean) => {
        if(isNextOpen || !isOpen) {
            return;
        }

        onOpenChange?.(false);
    };

    const onSheetClosed = (props: {
        id: string;
    }) => {
        setInternalSearchText("");

        setIsActive(false);

        if(bottomSheetProps?.onClosed) {
            bottomSheetProps.onClosed(props);
        }
    };

    const getFormattedCount = (value: number) => {
        return formatNumber({
            locale: activeLocale,
            value
        });
    };

    const getMinimumSelectionText = () => {
        return localize("minimum-selection-required", [
            getFormattedCount(minChoice ?? 0)
        ]);
    };

    const getCounterText = () => {
        if(currentSelection.maxChoice === undefined) {
            return localize("selected-count", [
                getFormattedCount(currentSelection.selectedCount)
            ]);
        }

        return localize("selected-count-with-max", [
            getFormattedCount(currentSelection.selectedCount),
            getFormattedCount(currentSelection.maxChoice)
        ]);
    };

    const getRowAccessibilityProps = (isSelected: boolean): SelectSheetRowAccessibilityType => {
        if(isMultipleSelect) {
            return {
                "aria-checked": isSelected,
                role: "checkbox"
            };
        }

        if(Platform.OS === "web") {
            return {
                "aria-pressed": isSelected,
                role: "button"
            };
        }

        return {
            "aria-selected": isSelected,
            role: "button"
        };
    };

    const getOkAccessibilityProps = () => {
        if(currentSelection.isMinChoiceMet) {
            return {};
        }

        if(Platform.OS === "web") {
            return {
                "aria-describedby": helperId
            };
        }

        return {
            accessibilityHint: getMinimumSelectionText()
        };
    };

    const getSearchInputProps = () => {
        if(Platform.OS !== "web") {
            return {};
        }

        return {
            "aria-controls": listId
        };
    };

    const renderLoadingIndicator = (size: LoadingSize, color: keyof NCoreUIKit.IconContentColors) => {
        if(LoadingIconComponentProp) {
            return <LoadingIconComponentProp
                customColor={colors.content.icon[color]}
                size={iconSizes[size]}
                color={color}
            />;
        }

        return <Loading
            customTheme={customTheme}
            color={color}
            size={size}
        />;
    };

    const renderLoading = () => {
        return <View
            aria-label={localize("loading")}
            style={[
                stylesheet.loading,
                loadingDynamicStyle
            ]}
            role="progressbar"
            accessible={true}
        >
            {renderLoadingIndicator("large", "default")}
        </View>;
    };

    const renderMoreLoading = () => {
        if(!isMoreLoading) {
            return null;
        }

        return <View
            style={[
                stylesheet.moreLoading,
                moreLoadingDynamicStyle
            ]}
            aria-live="polite"
        >
            {renderLoadingIndicator("small", "mid")}
            <Text
                customTheme={customTheme}
                variant="bodySmallSize"
                color="mid"
            >
                {localize("loading-more")}
            </Text>
        </View>;
    };

    const renderEmptyState = () => {
        if(isSearching) {
            return <Empty
                style={emptyStateDynamicStyle}
                customTheme={customTheme}
                variant="plain"
                role="status"
                size="small"
            >
                <Empty.Header>
                    <Empty.Media
                        icon={SearchFieldIcon}
                    />
                    <Empty.Title>
                        {localize("no-results")}
                    </Empty.Title>
                    <Empty.Description>
                        {localize("no-results-for-search", [
                            searchText
                        ])}
                    </Empty.Description>
                </Empty.Header>
                <Empty.Content>
                    <Button
                        title={localize("clear-search")}
                        onPress={handleClearSearch}
                        customTheme={customTheme}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                </Empty.Content>
            </Empty>;
        }

        return <Empty
            style={emptyStateDynamicStyle}
            customTheme={customTheme}
            variant="plain"
            role="status"
            size="small"
        >
            <Empty.Header>
                <Empty.Media/>
                <Empty.Title>
                    {localize("no-options")}
                </Empty.Title>
            </Empty.Header>
        </Empty>;
    };

    const renderSelectedMark = () => {
        return <View
            aria-hidden={true}
        >
            <CheckIcon
                color={colors.content.icon.high}
                size={iconSizes.medium}
            />
        </View>;
    };

    const renderRowLeading = (item: T & SelectedItem | SelectedItem, index: number, isSelected: boolean, isDisabled: boolean) => {
        const optionIcon = renderOptionIcon ? renderOptionIcon({
            index,
            item
        }) : null;

        if(!isMultipleSelect) {
            return optionIcon;
        }

        return <View
            style={[
                stylesheet.rowLeading,
                rowLeadingDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.checkBoxSlot,
                    checkBoxSlotDynamicStyle
                ]}
                aria-hidden={true}
            >
                <CheckBox
                    accessibilityLabel={item.__title}
                    customLocalize={customLocalize}
                    customTheme={customTheme}
                    isDisabled={isDisabled}
                    spreadBehaviour="free"
                    isChecked={isSelected}
                />
            </View>
            {optionIcon}
        </View>;
    };

    const renderDivider = (item: SelectedItem, previousItem?: SelectedItem) => {
        if(!previousItem) {
            return null;
        }

        const isVisible = getSelectSheetIsDividerVisible({
            previousKey: previousItem.__key,
            nextKey: item.__key,
            highlightedKeys
        });

        return <View
            style={isVisible ? dividerDynamicStyle : hiddenDividerDynamicStyle}
        />;
    };

    const renderRow = (item: T & SelectedItem | SelectedItem, index: number, isGroupStart = false) => {
        const rowKey = keyExtractor(item as T & SelectedItem, index);

        const isSelected = currentSelection.selectedKeys.has(item.__key);

        if(renderItem) {
            return renderItem({
                selectedItems: mainSelectedItems,
                item: item as T & SelectedItem,
                onSelect: (sItem) => {
                    selectObject(sItem);
                },
                setIsMoreLoading,
                setIsLoading,
                key: rowKey,
                isSelected,
                searchText,
                index,
                data
            });
        }

        const isDisabled = !!isMultipleSelect && !isSelected && currentSelection.isMaxChoiceReached;

        const isSelectedRow = !isMultipleSelect && isSelected;

        const isLeadingVisible = !!isMultipleSelect || !!renderOptionIcon;

        const selectedRowStyle = isSelectedRow ? {
            backgroundColor: colors.content.container[SELECT_SHEET_SELECTED_BACKGROUND]
        } : undefined;

        return <Fragment
            key={rowKey}
        >
            {renderDivider(item, isGroupStart ? undefined : currentData[index - 1])}
            <Item
                {...getRowAccessibilityProps(isSelected)}
                onPress={() => selectObject(item)}
                borderRadius={radiuses.none}
                customTheme={customTheme}
                isDisabled={isDisabled}
                style={[
                    selectedRowStyle,
                    rowDynamicStyle
                ]}
            >
                {renderRowMedia(item, index, isSelected, isDisabled, isLeadingVisible)}
                <Item.Content>
                    <Item.Title
                        numberOfLines={0}
                    >
                        <Text
                            variant={getItemTitleVariant({
                                titleVariant: SELECT_SHEET_ROW_TITLE_VARIANT,
                                isSelected: isSelectedRow && !isDisabled,
                                size: "medium"
                            })}
                            customColor={getRowTitleColor(isDisabled)}
                            customTheme={customTheme}
                        >
                            {item.__title}
                        </Text>
                    </Item.Title>
                </Item.Content>
                {renderRowActions(isSelectedRow)}
            </Item>
        </Fragment>;
    };

    const getRowTitleColor = (isDisabled: boolean): string => {
        if(isDisabled) {
            return colors.system.state.content.disabled.neutral;
        }

        return colors.content.text.high;
    };

    const renderRowMedia = (item: T & SelectedItem | SelectedItem, index: number, isSelected: boolean, isDisabled: boolean, isLeadingVisible: boolean) => {
        if(!isLeadingVisible) {
            return null;
        }

        return <Item.Media>
            {renderRowLeading(item, index, isSelected, isDisabled)}
        </Item.Media>;
    };

    const renderRowActions = (isSelectedRow: boolean) => {
        if(!isSelectedRow) {
            return null;
        }

        return <Item.Actions>
            {renderSelectedMark()}
        </Item.Actions>;
    };

    const renderGroups = () => {
        let offset = 0;

        return groups?.map((group, groupIndex) => {
            const groupOffset = offset;

            const headingId = `${baseId}-group-${groupIndex}`;

            offset += group.options.length;

            const firstItem = group.options[0];

            const previousItem = groups?.[groupIndex - 1]?.options.slice(-1)[0];

            return <Fragment
                key={`${group.heading ?? ""}-${groupOffset}`}
            >
                {firstItem && previousItem ? renderDivider(firstItem, previousItem) : null}
                <View
                    aria-labelledby={group.heading ? headingId : undefined}
                    role="group"
                >
                    {
                        group.heading ?
                            <PopoverSurfaceHeading
                                style={groupHeadingDynamicStyle}
                                customTheme={customTheme}
                                variant="sheet"
                                id={headingId}
                            >
                                {group.heading}
                            </PopoverSurfaceHeading>
                        :
                            null
                    }
                    {group.options.map((item, itemIndex) => renderRow(item, groupOffset + itemIndex, itemIndex === 0))}
                </View>
            </Fragment>;
        });
    };

    const renderOptions = () => {
        if(isLoading) {
            return renderLoading();
        }

        if(!currentData.length) {
            if(isMoreLoading) {
                return renderMoreLoading();
            }

            return renderEmptyState();
        }

        return <Fragment>
            <View
                aria-label={isSearching ? localize("search-results") : sheetTitle}
                style={stylesheet.list}
                role="group"
            >
                {groups ? renderGroups() : currentData.map((item: T & SelectedItem | SelectedItem, index: number) => renderRow(item, index))}
            </View>
            {renderMoreLoading()}
        </Fragment>;
    };

    const renderSearchInput = () => {
        if(!isSearchable) {
            return null;
        }

        return <TextInput
            {...getSearchInputProps()}
            onChangeText={handleSearchTextChange}
            placeholder={localize("search")}
            customLocalize={customLocalize}
            aria-label={localize("search")}
            customTheme={customTheme}
            spreadBehaviour="stretch"
            icon={SearchFieldIcon}
            isClearEnabled={true}
            autoCapitalize="none"
            enterKeyHint="search"
            ref={searchInputRef}
            autoCorrect={false}
            inputMode="search"
            value={searchText}
        />;
    };

    const renderSelectAll = () => {
        if(currentSelection.maxChoice !== undefined || !selectAll) {
            return null;
        }

        return <Button
            title={localize("select-all")}
            onPress={() => selectAll()}
            customTheme={customTheme}
            spreadBehaviour="free"
            icon={SelectAllIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderClearSelection = () => {
        if(minChoice || !clearSelection) {
            return null;
        }

        return <Button
            title={localize("clear-selection")}
            onPress={() => clearSelection()}
            customTheme={customTheme}
            spreadBehaviour="free"
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderTools = () => {
        if(!isToolsVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.tools,
                toolsDynamicStyle
            ]}
        >
            {renderSelectAll()}
            {renderClearSelection()}
            <Text
                style={stylesheet.counter}
                customTheme={customTheme}
                variant="bodySmallSize"
                aria-live="polite"
                color="mid"
            >
                {getCounterText()}
            </Text>
        </View>;
    };

    const renderControls = () => {
        if(!isControlsVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.controls,
                controlsDynamicStyle
            ]}
        >
            {renderSearchInput()}
            {renderTools()}
        </View>;
    };

    const renderFooterStatus = () => {
        if(!currentSelection.isMinChoiceMet) {
            return <View
                style={[
                    stylesheet.footerStatus,
                    footerStatusDynamicStyle
                ]}
            >
                <View
                    aria-hidden={true}
                >
                    <InfoIcon
                        color={colors.content.icon.mid}
                        size={iconSizes.small}
                    />
                </View>
                <Text
                    style={stylesheet.footerText}
                    customTheme={customTheme}
                    variant="bodyMediumSize"
                    id={helperId}
                    color="mid"
                >
                    {getMinimumSelectionText()}
                </Text>
            </View>;
        }

        if(!isMultipleSelect) {
            return null;
        }

        return <Text
            style={stylesheet.footerText}
            customTheme={customTheme}
            variant="bodySmallSize"
            color="mid"
        >
            {localize("selected-options-with-count", [
                getFormattedCount(currentSelection.selectedCount)
            ])}
        </Text>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footer,
                footerDynamicStyle
            ]}
        >
            {renderFooterStatus()}
            <View
                style={[
                    stylesheet.footerActions,
                    footerActionsDynamicStyle
                ]}
            >
                <Button
                    title={localize("cancel")}
                    customTheme={customTheme}
                    onPress={handleCancel}
                    spreadBehaviour="free"
                    variant="outline"
                    type="neutral"
                />
                <Button
                    {...getOkAccessibilityProps()}
                    isDisabled={!currentSelection.isMinChoiceMet}
                    title={localize("dialog-ok")}
                    customTheme={customTheme}
                    spreadBehaviour="free"
                    onPress={handleOk}
                />
            </View>
        </View>;
    };

    return <BottomSheet
        {...bottomSheetProps}
        modalProps={{
            ...bottomSheetProps?.modalProps,
            returnFocusRef: returnFocusRef ?? bottomSheetProps?.modalProps?.returnFocusRef
        }}
        scrollEndThreshold={moreLoadThreshold ?? bottomSheetProps?.scrollEndThreshold}
        renderBottom={isWorkWithRealtime ? undefined : renderFooter}
        key={customKey ? customKey : `${selectBoxKey}-bottomsheet`}
        description={description ?? bottomSheetProps?.description}
        isShowClose={bottomSheetProps?.isShowClose ?? true}
        onOpenChange={handleSheetOpenChange}
        customLocalize={customLocalize}
        onScrollEnd={onSheetScrollEnd}
        customTheme={customTheme}
        onClosed={onSheetClosed}
        customKey={selectBoxKey}
        isCustomPadding={true}
        isAutoHeight={true}
        title={sheetTitle}
        isOpen={isOpen}
        ref={sheetRef}
    >
        {renderControls()}
        <View
            id={listId}
        >
            {renderOptions()}
        </View>
    </BottomSheet>;
};
export default SelectSheet;
